import { useEffect, useState } from 'preact/hooks';
import { getCoachQueue, type CoachQueueItemApi } from '../lib/api';
import { ageInDays } from '../lib/aging';

// CoachQueue — the tapes waiting for this coach's notes (rung 2).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26` §8.2
//
// ── THE INSTRUMENT ──
//
// "A review request is an OBLIGATION with a date, so it takes the FAR-SIDE
// instrument: soonest first, loud when late, quiet when done. Do not invent an
// instrument; do not sort newest-first — that puts last month's wrap above
// tomorrow's deadline."
//
// ⚠ `orderShootDays` IS NOT REUSED HERE, and the reason is worth stating so
// the next reader does not think it was overlooked. That function takes
// `RecordRow[]` and keys on `fields.shoot_date`; a grant is not a record and
// has no fields. What IS reused is the primitive underneath both —
// `ageInDays` from lib/aging — and the ORDER, which the server already applies
// (`ORDER BY expires_at ASC`). Reimplementing the sort here would give the
// list two masters that could disagree.
//
// ⚠ `ageInDays` counts BACKWARDS: a future date is NEGATIVE. -1 is tomorrow,
// 0 is today, +2 is two days ago. That sign is the single easiest thing to get
// wrong on this screen, and getting it wrong inverts "urgent" and "relaxed".

export interface CoachQueueProps {
  /** Injectable so a test can assert the bands without waiting for midnight —
   * the same courtesy farSide.ts extends to its own callers. */
  now?: Date;
}

type Urgency = 'overdue' | 'today' | 'soon' | 'later';

/** How loudly this row should read. */
export function urgencyOf(expiresAt: string, now: Date = new Date()): Urgency {
  const age = ageInDays(expiresAt, now);
  if (age === null) return 'later';
  // age > 0 means the date has PASSED.
  if (age > 0) return 'overdue';
  if (age === 0) return 'today';
  return -age <= 2 ? 'soon' : 'later';
}

/** The words a coach reads. Deliberately plain: a countdown in hours would
 * imply a precision the expiry does not have. */
export function dueLabel(expiresAt: string, now: Date = new Date()): string {
  const age = ageInDays(expiresAt, now);
  if (age === null) return '';
  if (age > 0) return 'expired';
  if (age === 0) return 'today';
  if (age === -1) return 'tomorrow';
  return `in ${-age} days`;
}

const QUEUE_FRAME = 'We couldn’t load what’s waiting for you.';

export function CoachQueue({ now }: CoachQueueProps) {
  const [items, setItems] = useState<CoachQueueItemApi[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCoachQueue()
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // ⚑ AN EMPTY QUEUE AND A BROKEN QUEUE MUST NEVER LOOK THE SAME. This
        // surface's whole failure mode is a list that says nothing when it
        // should say something — the owner-pool zero-rows bug would have
        // rendered exactly the same "nothing waiting" as a genuinely clear
        // desk. So a failure is stated, never absorbed into emptiness.
        setError(err instanceof Error ? err.message : QUEUE_FRAME);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div class="coach-queue">
        {/* The frame once, the server's sentence only when it adds one —
            never the same fact twice in a row. */}
        <p class="coach__quiet">{error === QUEUE_FRAME ? QUEUE_FRAME : `${QUEUE_FRAME} ${error}`}</p>
      </div>
    );
  }

  if (items === null) {
    return (
      <div class="coach-queue">
        <p class="coach__quiet">Looking for tapes…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div class="coach-queue">
        {/* Says what WILL happen, not merely that there is nothing — an empty
            state that only says "nothing here" reads as a dead end. */}
        <p class="coach__quiet">Nothing waiting. Tapes families send you will appear here.</p>
      </div>
    );
  }

  return (
    <>
    <ul class="coach-queue" aria-label="Tapes waiting for your notes">
      {items.map((item) => {
        const urgency = urgencyOf(item.expiresAt, now);
        return (
          <li key={item.grantId} class={`coach-queue__row coach-queue__row--${urgency}`}>
            <div class="coach-queue__who">
              {/* The role is what a coach actually recognises a tape by;
                  the project is the context underneath it. Either may be
                  missing — a household is not obliged to have filled both in
                  before asking for notes. */}
              <span class="coach-queue__role">{item.role ?? 'A tape'}</span>
              {item.project ? <span class="coach-queue__project">{item.project}</span> : null}
            </div>
            <span class={`coach-queue__due coach-queue__due--${urgency}`}>
              {dueLabel(item.expiresAt, now)}
            </span>
          </li>
        );
      })}
    </ul>
    {/* ⚠ A ROW IS NOT A DOOR, AND SAYING SO BEATS PRETENDING. The tape
        opens only through the family's own link: the grant token is the
        sole credential and the server keeps a HASH of it
        (coachGrants.ts / grants.ts), so no signed-in read can rebuild the
        URL — and minting a fresh token here would make this surface the
        one doing the granting, which spec §7 forbids. Until a
        session-authed review door exists, the queue says where the work
        actually happens rather than offering rows that go nowhere on tap. */}
    <p class="coach__quiet">
      Each tape opens from the link its family sent you — notes you leave there land in your
      history here.
    </p>
    </>
  );
}
